import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import logoUrl from "../components/logo.png"; // Company logo — placed at A1:A3

// ─── Date helpers ─────────────────────────────────────────────────────────────

const fmtDate = (ds) => {
  if (!ds) return "";
  try {
    const d = new Date(ds);
    return isNaN(d.getTime())
      ? String(ds)
      : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return String(ds); }
};

const fmtDateFile = (ds) => {
  if (!ds) return new Date().toISOString().split("T")[0];
  try {
    const d = new Date(ds);
    return isNaN(d.getTime())
      ? new Date().toISOString().split("T")[0]
      : d.toISOString().split("T")[0];
  } catch { return new Date().toISOString().split("T")[0]; }
};

/**
 * Returns the string value, or "NA" if the value is empty/null/undefined.
 * Used for DATA cells where empty should show "NA".
 */
const NA = (v) => {
  if (v === undefined || v === null || String(v).trim() === "") return "NA";
  return String(v);
};

/**
 * Returns the string value, or "" if the value is empty/null/undefined.
 * Used for LABEL cells and header fields where we want a true blank.
 */
const S = (v) => (v !== undefined && v !== null ? String(v) : "");

const safeJSON = (val, def) => {
  if (!val) return def;
  if (typeof val === "object") return val;
  try { return JSON.parse(val); } catch { return def; }
};

// ─── Section data normaliser ──────────────────────────────────────────────────

const parseSection = (raw) => {
  const d = raw || {};
  return {
    tcds_no:              d.tcds_no              || d.section_number     || "",
    revision_no:          d.revision_no          ?? d.rev_no             ?? 0,
    section_number:       d.section_number       || "",
    section_title:        d.section_title        || "",
    section_for:          d.section_for          || "",
    tcds_type:            d.tcds_type            || "Automation/Standard",
    customer_name:        d.customer_name        || d.customer           || "",
    purchase_order_no:    d.purchase_order_no    || d.po_no              || "",
    po_date:              d.po_date              || "",
    po_received_date:     d.po_received_date     || d.po_received_on     || "",
    project_name:         d.project_name         || "",
    project_no:           d.project_no           || "",
    offer_no:             d.offer_no             || "",
    offer_date:           d.offer_date           || "",
    tcds_release_date:    d.tcds_release_date    || d.tcds_released_on   || "",
    crm_date:             d.crm_date             || "",
    cdd:                  d.cdd                  || "",
    ld:                   d.ld                   || "",
    regd_office_address:  d.regd_office_address  || "",
    product_fg_code:      d.product_fg_code      || "",
    automated_valves_qty: d.automated_valves_qty != null ? String(d.automated_valves_qty) : "",
    manual_valves_qty:    d.manual_valves_qty    != null ? String(d.manual_valves_qty)    : "",
    spares_qty:           d.spares_qty           != null ? String(d.spares_qty)           : "",
    quantity:             d.quantity             != null ? String(d.quantity)             : "",
    make:                 d.make                 || "",
    series:               d.series               || "",
    type:                 d.type                 || "",
    end_connection:       d.end_connection       || "",
    size:                 d.size                 || "",
    class:                d.class                || "",
    body_material:        d.body_material        || d.body               || "",
    disc_material:        d.disc_material        || d.disc               || "",
    stem_material:        d.stem_material        || d.stem               || "",
    seat_material:        d.seat_material        || d.seat               || "",
    operator:             d.operator             || "",
    painting_details:     d.painting_details     || "",
    flow_direction:       d.flow_direction       || "",
    operation:            d.operation            || "",
    air_fail_action:      d.air_fail_action      || "",
    power_fail_action:    d.power_fail_action    || "",
    opening_time:         d.opening_time         || "",
    closing_time:         d.closing_time         || "",
    gad:                  d.gad                  || "",
    qap:                  d.qap                  || "",
    mor:                  d.mor                  || "",
    model:                d.model                || "",
    moc:                  d.moc                  || "",
    other_details:        d.other_details        || "",
    special_testing:      d.special_testing      || "",
    certification:        d.certification        || "",
    customer_item_code:   d.customer_item_code   || "",
    related_automation_pkg: d.related_automation_pkg || "",
    notes:                d.notes                || "",
    opr_pressure:         d.opr_pressure         || "",
    max_pressure:         d.max_pressure         || d.max_press          || "",
    min_pressure:         d.min_pressure         || d.min_press          || "",
    max_temp:             d.max_temp             || "",
    min_temp:             d.min_temp             || "",
    dynamic_parameters:   safeJSON(d.dynamic_parameters, {}),
    accessories_data:     safeJSON(d.accessories_data,   []),
    tag_numbers:          Array.isArray(d.tag_numbers)
                            ? d.tag_numbers
                            : safeJSON(d.tag_numbers, []),
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// STYLE CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const GREY  = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9D9D9" } };
const WHITE = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };

const BD_MED = {
  top:    { style: "medium", color: { argb: "FF000000" } },
  left:   { style: "medium", color: { argb: "FF000000" } },
  bottom: { style: "medium", color: { argb: "FF000000" } },
  right:  { style: "medium", color: { argb: "FF000000" } },
};

const dataBorder = (c1, c2) => ({
  top:    { style: "hair",                       color: { argb: "FF000000" } },
  left:   { style: c1 === 1 ? "medium" : "thin", color: { argb: "FF000000" } },
  bottom: { style: "hair",                       color: { argb: "FF000000" } },
  right:  { style: c2 === 8 ? "medium" : "thin", color: { argb: "FF000000" } },
});

const HF = (sz = 13) => ({ name: "Cambria",      size: sz, bold: true  });
const PF = (sz = 12) => ({ name: "Calibri",      size: sz, bold: true  });
const CF = (sz = 12) => ({ name: "Calibri",      size: sz, bold: false });
const VF = (sz = 12) => ({ name: "Aptos Narrow", size: sz, bold: false });

const LA = () => ({ horizontal: "left",   vertical: "center", wrapText: true });
const CA = () => ({ horizontal: "center", vertical: "center", wrapText: true });
const RA = () => ({ horizontal: "right",  vertical: "center", wrapText: true });

// ═══════════════════════════════════════════════════════════════════════════════
// CELL HELPER — every cell routes through here; no cell is ever border-less
// ═══════════════════════════════════════════════════════════════════════════════

const mc = (ws, r1, c1, r2, c2, value, font, align, fill, border, rh) => {
  if (r1 !== r2 || c1 !== c2) {
    try { ws.mergeCells(r1, c1, r2, c2); } catch (_) {}
  }
  const cell     = ws.getCell(r1, c1);
  cell.value     = value;
  cell.font      = font;
  cell.alignment = align;
  cell.border    = border ?? dataBorder(c1, c2);
  if (fill) cell.fill = fill;
  if (rh)   ws.getRow(r1).height = rh;
};

// ── Shorthand writers ─────────────────────────────────────────────────────────

/** Label cell — Calibri Bold, GREY fill */
const lbl = (ws, r, c1, c2, text) =>
  mc(ws, r, c1, r, c2, text, PF(12), LA(), GREY, dataBorder(c1, c2));

/** Value cell — Aptos Narrow, WHITE fill, "NA" when empty */
const val = (ws, r, c1, c2, v) =>
  mc(ws, r, c1, r, c2, NA(v), VF(12), LA(), WHITE, dataBorder(c1, c2));

/** Section / banner header — Cambria Bold, WHITE fill, medium border */
const secHdr = (ws, r, c1, c2, text, sz = 12) =>
  mc(ws, r, c1, r, c2, text, HF(sz), CA(), WHITE, BD_MED, 16.5);

// ═══════════════════════════════════════════════════════════════════════════════
// ACCESSORY HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Find an accessory by name from the accessories_data array (case-insensitive
 * substring match).  Returns undefined when not found.
 */
const findAcc = (accessories, name) => {
  const n = name.toLowerCase();
  return (accessories || []).find((a) => {
    const aName = (a.accessoryName || a.accessory_name || a.name || "").toLowerCase();
    return aName.includes(n) || n.includes(aName);
  });
};

/**
 * Find an accessory by EXACT name match (case-insensitive, trimmed).
 * Used for accessories whose names share common substrings (e.g. the two
 * Gland/Blind-plug variants) to prevent one from matching the other's row.
 */
const findAccExact = (accessories, name) => {
  const n = name.toLowerCase().trim();
  return (accessories || []).find((a) => {
    const aName = (a.accessoryName || a.accessory_name || a.name || "").toLowerCase().trim();
    return aName === n;
  });
};

/**
 * Read a parameter value from an accessory entry.
 *
 * TCDSForm saves accessories in one of two shapes:
 *   • Array format (current):  parameters = [{id, name, value, ...}, ...]
 *   • Flat-object format (legacy / hand-crafted): parameters = { MAKE: "...", MODEL: "..." }
 *
 * This helper tries the array format first, then the flat-object format, and
 * finally falls back to the top-level `model` field when the caller asks for
 * MODEL / SERIES / MAKE and no parameter entry was found.
 *
 * Returns "NA" when nothing is found.
 */
const accP = (acc, paramName) => {
  if (!acc) return "NA";
  const lc = paramName.toLowerCase().trim();

  // ── 1. Array format: parameters = [{id, name, value}, ...] ─────────────────
  if (Array.isArray(acc.parameters)) {
    for (const p of acc.parameters) {
      const pName = (p.name || "").toLowerCase().trim();
      if (pName === lc && p.value != null && String(p.value).trim() !== "") {
        return String(p.value);
      }
    }
    // Parameter found in list but value is empty → fall through to model fallback
  }

  // ── 2. Flat-object format: parameters = { MAKE: "...", MODEL: "..." } ───────
  if (acc.parameters && typeof acc.parameters === "object" && !Array.isArray(acc.parameters)) {
    const p = acc.parameters;
    if (p[paramName]               != null && String(p[paramName]).trim()               !== "") return String(p[paramName]);
    if (p[paramName.toLowerCase()] != null && String(p[paramName.toLowerCase()]).trim() !== "") return String(p[paramName.toLowerCase()]);
    if (p[paramName.toUpperCase()] != null && String(p[paramName.toUpperCase()]).trim() !== "") return String(p[paramName.toUpperCase()]);
    for (const [k, v] of Object.entries(p)) {
      if (k.toLowerCase() === lc && v != null && String(v).trim() !== "") return String(v);
    }
  }

  // ── 3. Top-level model / name fallback ──────────────────────────────────────
  //    Some accessory entries only have a `model` field and no detailed parameters.
  if (["model", "series", "make"].includes(lc) && acc.model && String(acc.model).trim() !== "") {
    return String(acc.model);
  }

  return "NA";
};

/**
 * Read a value from the section's dynamic_parameters.
 * Strips whitespace / dots / dashes / underscores before comparing.
 * Returns "NA" when not found.
 */
const dynP = (d, paramName) => {
  const dp = d.dynamic_parameters || {};
  const norm = (s) => s.toLowerCase().replace(/[\s._-]/g, "");
  const n = norm(paramName);

  if (dp[paramName]               != null && String(dp[paramName]).trim()               !== "") return String(dp[paramName]);
  if (dp[paramName.toLowerCase()] != null && String(dp[paramName.toLowerCase()]).trim() !== "") return String(dp[paramName.toLowerCase()]);
  if (dp[paramName.toUpperCase()] != null && String(dp[paramName.toUpperCase()]).trim() !== "") return String(dp[paramName.toUpperCase()]);

  for (const [k, v] of Object.entries(dp)) {
    if (norm(k) === n && v != null && String(v).trim() !== "") return String(v);
  }
  return "NA";
};

// ═══════════════════════════════════════════════════════════════════════════════
// SHEET SETUP
// ═══════════════════════════════════════════════════════════════════════════════

const setupSheet = (ws) => {
  [20.67, 23.67, 25.67, 29.44, 20.67, 23.67, 20.67, 23.67].forEach(
    (w, i) => { ws.getColumn(i + 1).width = w; }
  );
  Object.assign(ws.pageSetup, {
    paperSize: 9, orientation: "portrait",
    fitToPage: true, fitToWidth: 1, fitToHeight: 0,
    margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
  });
};

// ═══════════════════════════════════════════════════════════════════════════════
// LOGO LOADER — converts the imported PNG URL to an ArrayBuffer
// ═══════════════════════════════════════════════════════════════════════════════

const loadLogoBuffer = async () => {
  try {
    const res = await fetch(logoUrl);
    if (!res.ok) return null;
    const ab  = await res.arrayBuffer();
    return { buffer: ab, extension: "png" };
  } catch (err) {
    console.warn("[generateTCDSExcel] Could not load logo:", err.message);
    return null;
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// ROW HELPER — 4 paired [label | value] columns across 8 columns
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Draws one data row split into 4 label+value pairs.
 * Labels use GREY fill + bold Calibri; values use WHITE fill + Aptos Narrow.
 * Empty labels render as empty white cells; values always show "NA" when blank.
 */
const row4 = (ws, r, lbls, vals) => {
  lbls.forEach((label, i) => {
    const c = i * 2 + 1;
    mc(ws, r, c,   r, c,   label || "", label ? PF(12) : VF(12), LA(), label ? GREY : WHITE, dataBorder(c,   c));
    mc(ws, r, c+1, r, c+1, NA(vals[i]), VF(12),                   LA(), WHITE,                dataBorder(c+1, c+1));
  });
  ws.getRow(r).height = 15.75;
};

// ═══════════════════════════════════════════════════════════════════════════════
// DRAW ONE COMPLETE SECTION ON A WORKSHEET
// ═══════════════════════════════════════════════════════════════════════════════

const drawSectionOnSheet = (ws, d, logoImageId) => {
  let R   = 1;
  const accs = d.accessories_data || [];

  // ── Rows 1-3: Company / Logo header ──────────────────────────────────────
  try { ws.mergeCells(1, 1, 3, 1); } catch (_) {}
  const markCell     = ws.getCell(1, 1);
  markCell.border    = BD_MED;
  markCell.alignment = CA();
  markCell.fill      = WHITE;
  ws.getRow(1).height = 15;
  ws.getRow(2).height = 15;
  ws.getRow(3).height = 15;

if (logoImageId !== null && logoImageId !== undefined) {
    ws.addImage(logoImageId, {
      tl: { col: 0.32, row: 0.108 },
      ext: { width: 108, height: 56 },
      editAs: "oneCell",
    });
    markCell.value = "";
  }

  // B1:F3 – Company name
  mc(ws, 1, 2, 3, 6,
    "INTEGRAL PROCESS CONTROLS INDIA PVT. LTD.\nBHOSARI, PUNE – 411026",
    HF(17), CA(), WHITE, BD_MED);

  // G/H 1-3 – TCDS metadata
  mc(ws, 1, 7, 1, 7, "TCDS No",    HF(12), LA(), GREY, dataBorder(7, 7));
  val(ws, 1, 8, 8, d.tcds_no);
  mc(ws, 2, 7, 2, 7, "REV. No.",   HF(12), LA(), GREY, dataBorder(7, 7));
  val(ws, 2, 8, 8, d.revision_no);
  mc(ws, 3, 7, 3, 7, "TCDS Date",  HF(12), LA(), GREY, dataBorder(7, 7));
  val(ws, 3, 8, 8, fmtDate(d.tcds_release_date) || "");
  R += 3;

  // ── Row 4: Title ──────────────────────────────────────────────────────────
  mc(ws, R, 1, R, 1, "Mark Dept.", HF(12), LA(), GREY, dataBorder(1, 1));
  mc(ws, R, 2, R, 6,
    "Techno Commercial Data Sheet And Contract Review Sheet",
    HF(16), CA(), WHITE, BD_MED, 20.25);
  mc(ws, R, 7, R, 7, "CRM Date",  HF(12), LA(), GREY, dataBorder(7, 7));
  val(ws, R, 8, 8, fmtDate(d.crm_date) || "");
  R++;

  // ── Row 5: Separator ──────────────────────────────────────────────────────
  mc(ws, R, 1, R, 8, "", VF(11), LA(), WHITE, BD_MED, 29.25);
  R++;

  // ── Row 6: Customer ───────────────────────────────────────────────────────
  mc(ws, R, 1, R, 2, "CUSTOMER:", PF(12), LA(), GREY, dataBorder(1, 2));
  mc(ws, R, 3, R, 8, NA(d.customer_name), CF(12), LA(), WHITE, dataBorder(3, 8));
  R++;

  // ── Row 7: Purchase Order ─────────────────────────────────────────────────
  mc(ws, R, 1, R, 2, "PURCHASE ORDER NO.:", PF(12), LA(), GREY, dataBorder(1, 2));
  mc(ws, R, 3, R, 3, NA(d.purchase_order_no), CF(12), LA(), WHITE, dataBorder(3, 3));
  lbl(ws, R, 4, 4, "PO DATE:");
  mc(ws, R, 5, R, 5, NA(fmtDate(d.po_date) || ""), CF(12), LA(), WHITE, dataBorder(5, 5));
  lbl(ws, R, 6, 6, "PO / LOI RECEIVED DATE:");
  mc(ws, R, 7, R, 8, NA(fmtDate(d.po_received_date) || ""), CF(12), LA(), WHITE, dataBorder(7, 8));
  ws.getRow(R).height = 31.5;
  R++;

  // ── Row 8: Project ────────────────────────────────────────────────────────
  mc(ws, R, 1, R, 2, "PROJECT NAME:", PF(12), LA(), GREY, dataBorder(1, 2));
  mc(ws, R, 3, R, 5, NA(d.project_name), CF(12), LA(), WHITE, dataBorder(3, 5));
  lbl(ws, R, 6, 6, "PROJECT NO.:");
  mc(ws, R, 7, R, 8, NA(d.project_no), CF(12), LA(), WHITE, dataBorder(7, 8));
  R++;

  // ── Row 9: Offer ──────────────────────────────────────────────────────────
  mc(ws, R, 1, R, 2, "OFFER NO.:", PF(12), LA(), GREY, dataBorder(1, 2));
  mc(ws, R, 3, R, 5, NA(d.offer_no), CF(12), LA(), WHITE, dataBorder(3, 5));
  lbl(ws, R, 6, 6, "OFFER DATE:");
  mc(ws, R, 7, R, 8, NA(fmtDate(d.offer_date) || ""), CF(12), LA(), WHITE, dataBorder(7, 8));
  R++;

  // ── Row 10: Dates / CDD / LD ──────────────────────────────────────────────
  mc(ws, R, 1, R, 2, "TCDS RELEASE DATE:", PF(12), LA(), GREY, dataBorder(1, 2));
  mc(ws, R, 3, R, 3, NA(fmtDate(d.tcds_release_date) || ""), CF(12), LA(), WHITE, dataBorder(3, 3));
  lbl(ws, R, 4, 4, "CDD:");
  mc(ws, R, 5, R, 5, NA(fmtDate(d.cdd) || ""),  CF(12), RA(), WHITE, dataBorder(5, 5));
  lbl(ws, R, 6, 6, "LD:");
  mc(ws, R, 7, R, 8, NA(d.ld),                  CF(12), RA(), WHITE, dataBorder(7, 8));
  R++;

  // ── Rows 11-12: Regd. Office Address ─────────────────────────────────────
  mc(ws, R, 1, R+1, 2, "REGD. OFFICE ADDRESS:", PF(12), LA(), GREY, dataBorder(1, 2));
  mc(ws, R,   3, R,   8, NA(d.regd_office_address), CF(12), LA(), WHITE, dataBorder(3, 8));
  mc(ws, R+1, 3, R+1, 8, "",                        CF(12), LA(), WHITE, dataBorder(3, 8));
  R += 2;

  // ── Row 13: Quantities ────────────────────────────────────────────────────
  mc(ws, R, 1, R, 3, "AUTOMATED VALVES QTY:", PF(12), LA(), GREY, dataBorder(1, 3));
  mc(ws, R, 4, R, 4, NA(d.automated_valves_qty), CF(12), LA(), WHITE, dataBorder(4, 4));
  mc(ws, R, 5, R, 5, "MANUAL VALVES QTY:", PF(12), LA(), GREY, dataBorder(5, 5));
  mc(ws, R, 6, R, 6, NA(d.manual_valves_qty), CF(12), LA(), WHITE, dataBorder(6, 6));
  lbl(ws, R, 7, 7, "SPARES QTY:");
  mc(ws, R, 8, R, 8, NA(d.spares_qty), CF(12), LA(), WHITE, dataBorder(8, 8));
  R++;

  // ── Row 14: Product FG Code ───────────────────────────────────────────────
  mc(ws, R, 1, R, 2, "PRODUCT FG CODE:", PF(12), LA(), GREY, dataBorder(1, 2));
  mc(ws, R, 3, R, 6, NA(d.product_fg_code), CF(12), LA(), WHITE, dataBorder(3, 6));
  lbl(ws, R, 7, 7, "SECTION FOR:");
  mc(ws, R, 8, R, 8, NA(d.section_for), CF(12), LA(), WHITE, dataBorder(8, 8));
  R++;

  // ── Row 15: Section banner ────────────────────────────────────────────────
  const sectionBanner = [d.section_number, d.section_title].filter(Boolean).join(" – ") || "SECTION DATA";
  mc(ws, R, 1, R, 8, sectionBanner, HF(13), CA(), WHITE, BD_MED, 16.5);
  R++;

  // ── Row 16: Valve type + MOR ──────────────────────────────────────────────
  const vtName = [d.make, d.type].filter(Boolean).join(" ") || "VALVE";
  mc(ws, R, 1, R, 6, vtName.toUpperCase(), HF(12), CA(), WHITE, BD_MED, 16.5);
  mc(ws, R, 7, R, 8, "MOR",               HF(12), CA(), WHITE, BD_MED, 16.5);
  R++;

  // ── Rows 17-27: Valve specifications grid ─────────────────────────────────
  const valveRows = [
    ["MAKE",                    d.make,          "TRIM NO",               dynP(d,"Trim No"),                 "PAINTING DETAILS",   d.painting_details,    "MAKE",  d.mor],
    ["SERIES",                  d.series,        "PLUG",                  dynP(d,"Plug"),                    "FLOW DIRECTION",     d.flow_direction,      "MODEL", d.model],
    ["TYPE",                    d.type,          "SEAT",                  d.seat_material,                   "OPERATION",          d.operation,           "MOC",   d.moc],
    ["MANUFACTURING\nSTANDARD", dynP(d,"Manufacturing Standard"), "STEM", d.stem_material,                   "AIR FAIL ACTION",    d.air_fail_action,     "OTHER", d.other_details],
    ["F TO F DIMENSIONS\n/ STD.",dynP(d,"F to F Dimensions"),   "GASKET & PACKING", dynP(d,"Gasket & Packing"),"POWER FAIL ACTION",d.power_fail_action,  "",      ""],
    ["TESTING STD.",            dynP(d,"Testing Std"),           "BOLTING",          dynP(d,"Bolting"),        "OPENING TIME",       d.opening_time,        "",      ""],
    ["PORT",                    dynP(d,"Port"),                  "OPERATOR",         d.operator,               "CLOSING TIME",       d.closing_time,        "",      ""],
    ["END CONNECTION",          d.end_connection,                "SPECIAL TESTING",  d.special_testing,        "GAD",                d.gad,                 "",      ""],
    ["SIZE",                    dynP(d,"Size") !== "NA" ? dynP(d,"Size") : (d.size || ""),  "SPECIAL REQUIREMENT",dynP(d,"Special Requirement"),"QAP",         d.qap,                 "",      ""],
    ["CLASS",                   dynP(d,"Class") !== "NA" ? dynP(d,"Class") : (d.class || ""),  "RT QTY PERCENTAGE",dynP(d,"RT Qty Percentage"),  "QUANTITY",     d.quantity,            "",      ""],
    ["BODY & BONNET",           d.body_material,                 "",                 "",                       "CUSTOMER ITEM CODE", d.customer_item_code,  "",      ""],
  ];

  valveRows.forEach((row) => {
    for (let i = 0; i < 8; i += 2) {
      const label = row[i];
      const rawVal = row[i + 1];
      const c = i + 1;
      mc(ws, R, c,   R, c,   label || "", label ? PF(12) : VF(12), LA(), label ? GREY : WHITE, dataBorder(c,   c));
      // Value cells: show NA when empty only if there's a label; truly blank label cells stay blank
      mc(ws, R, c+1, R, c+1, label ? NA(rawVal) : S(rawVal), VF(12), LA(), WHITE, dataBorder(c+1, c+1));
    }
    ws.getRow(R).height = 15.75;
    R++;
  });

  // ═════════════════════════════════════════════════════════════════════════
  // RESOLVE ACCESSORIES
  // ═════════════════════════════════════════════════════════════════════════

  const actuatorAcc = findAcc(accs, "Actuator");
  const bracketAcc  = findAcc(accs, "Bracket")    || findAcc(accs, "Coupling");
  const afrAcc      = findAcc(accs, "Air Filter") || findAcc(accs, "Regulator") || findAcc(accs, "AFR");
  const tubingAcc   = findAcc(accs, "Tubing");
  const fittingAcc  = findAcc(accs, "Fitting");
  const svAcc       = findAcc(accs, "Solenoid");
  const lsbAcc      = findAcc(accs, "Limit Switch");
  const qevAcc      = findAcc(accs, "Quick Exhaust") || findAcc(accs, "QEV");
  const pilotAcc    = findAcc(accs, "Pilot")          || findAcc(accs, "Volume Booster");
  const posAcc      = findAcc(accs, "Positioner");
  const fusibleAcc  = findAcc(accs, "Fusible");
  const fcvAcc      = findAcc(accs, "Flow Control");
  const edvAcc      = findAcc(accs, "Exhaust Diverter");
  const jbAcc       = findAcc(accs, "Junction Box");
  const airlockAcc  = findAcc(accs, "Airlock");

  // ── Gland/Blind-plug accessories — matched by EXACT name so they never
  //    bleed across rows.  Each is scoped to its own valve section.
  const cgSolenoidAcc    = findAccExact(accs, "Gland, Blind plug & cables - Solenoid Valve");
  const cgLimitSwitchAcc = findAccExact(accs, "Gland, Blind plug & cables - Limit Switch Valve");

  // ── ACTUATOR + BRACKET & COUPLING ────────────────────────────────────────
  mc(ws, R, 1, R, 6, "ACTUATOR",                       HF(12), CA(), WHITE, BD_MED, 16.5);
  mc(ws, R, 7, R, 8, "BRACKET + COUPLING (MOUNTING)",  HF(12), CA(), WHITE, BD_MED, 16.5);
  R++;

  [
    ["MAKE",      "SPRING RATING",   "AREA OF\nCLASSIFICATION", "BRACKET MOC"],
    ["SERIES",    "BODY MATERIAL",   "CERTIFICATION",            "COUPLING MOC"],
    ["TYPE",      "SUPPLY VOLTAGE",  "OFFER NO. / OTHER",        "FASTNERS MOC"],
    ["OPERATION", "ACTUATOR COLOUR", "AIR SUPPLY PRESSURE",      "STEM EXTENSION"],
    ["MODEL",     "END CAP COLOUR",  "",                         ""],
  ].forEach((labels) => {
    row4(ws, R, labels, [
      accP(actuatorAcc, labels[0]),
      accP(actuatorAcc, labels[1]),
      accP(actuatorAcc, labels[2].replace(/\n/g, " ")),
      accP(bracketAcc,  labels[3]),
    ]);
    R++;
  });

  // ── AIR FILTER REGULATOR + TUBING & FITTINGS ─────────────────────────────
  mc(ws, R, 1, R, 4, "AIR FILTER REGULATOR", HF(12), CA(), WHITE, BD_MED, 16.5);
  mc(ws, R, 5, R, 8, "TUBING & FITTINGS",    HF(12), CA(), WHITE, BD_MED, 16.5);
  R++;

  [
    ["MAKE",                "PORT CONNECTION", "TUBING MAKE", "FITTING MAKE"],
    ["MODEL",               "GAUGE MOC",       "TUBING MOC",  "FITTING MOC"],
    ["BODY MOC",            "DRAIN TYPE",      "TUBING SIZE", ""],
    ["FILTER ELEMENT MOC",  "CERTIFICATION",   "",            ""],
    ["FILTER ELEMENT SIZE", "OTHER",           "",            ""],
  ].forEach((labels) => {
    row4(ws, R, labels, [
      accP(afrAcc,     labels[0]),
      accP(afrAcc,     labels[1]),
      accP(tubingAcc,  labels[2]),
      accP(fittingAcc, labels[3]),
    ]);
    R++;
  });

  // ── SOLENOID VALVE + CABLE GLAND (Solenoid-specific) ─────────────────────
  mc(ws, R, 1, R, 6, "SOLENOID VALVE",                   HF(12), CA(), WHITE, BD_MED, 16.5);
  mc(ws, R, 7, R, 8, "CABLE GLAND, BLIND PLUG & CABLES", HF(12), CA(), WHITE, BD_MED, 16.5);
  R++;

  [
    ["MAKE",                    "COIL VOLTAGE",     "BUG SCREEN",    "CABLE GLAND TYPE"],
    ["VALVE TYPE",              "PORT END",         "CERTIFICATION", "CABLE GLAND MOC"],
    ["MODEL",                   "BODY MOC",         "OTHER",         "CABLE GLAND SIZE"],
    ["MOR TYPE",                "CABLE ENTRY SIZE", "QTY",           "CABLE GLAND AREA OF\nCLASS."],
    ["AREA OF\nCLASSIFICATION", "MOUNTING TYPE",    "",              "CABLE REQUIRED"],
  ].forEach((labels) => {
    row4(ws, R, labels, [
      accP(svAcc,           labels[0].replace(/\n/g, " ")),
      accP(svAcc,           labels[1]),
      accP(svAcc,           labels[2]),
      accP(cgSolenoidAcc,   labels[3].replace(/\n/g, " ")),
    ]);
    R++;
  });

  // ── LIMIT SWITCH BOX + CABLE GLAND (Limit-Switch-specific) ──────────────
  mc(ws, R, 1, R, 6, "LIMIT SWITCH BOX",                 HF(12), CA(), WHITE, BD_MED, 16.5);
  mc(ws, R, 7, R, 8, "CABLE GLAND, BLIND PLUG & CABLES", HF(12), CA(), WHITE, BD_MED, 16.5);
  R++;

  [
    ["MAKE",                    "SWITCH/SENSOR MAKE",  "LSB BLIND PLUG",  "CABLE GLAND TYPE"],
    ["MODEL",                   "SWITCH/SENSOR MODEL", "CERTIFICATE",     "CABLE GLAND MOC"],
    ["ENCLOSURE MOC",           "SWITCH/SENSOR QTY",   "OTHER",           "CABLE GLAND SIZE"],
    ["AREA OF\nCLASSIFICATION", "SUPPLY VOLTAGE",      "SPE. BLIND PLUG", "CABLE GLAND AREA OF\nCLASS."],
    ["SWITCH/SENSOR TYPE",      "CABLE ENTRY SIZE",    "",                "CABLE REQUIRED"],
  ].forEach((labels) => {
    row4(ws, R, labels, [
      accP(lsbAcc,           labels[0].replace(/\n/g, " ")),
      accP(lsbAcc,           labels[1]),
      accP(lsbAcc,           labels[2]),
      accP(cgLimitSwitchAcc, labels[3].replace(/\n/g, " ")),
    ]);
    R++;
  });

  // ── QEV / PILOT / POSITIONER / FUSIBLE LINK ──────────────────────────────
  mc(ws, R, 1, R, 2, "QUICK EXHAUST VALVE",          HF(12), CA(), WHITE, BD_MED, 16.5);
  mc(ws, R, 3, R, 4, "PILOT VALVE / VOLUME BOOSTER", HF(12), CA(), WHITE, BD_MED, 16.5);
  mc(ws, R, 5, R, 6, "POSITIONER",                   HF(12), CA(), WHITE, BD_MED, 16.5);
  mc(ws, R, 7, R, 8, "FUSIBLE LINK OR PLUG",         HF(12), CA(), WHITE, BD_MED, 16.5);
  R++;

  const fourAccs = [qevAcc, pilotAcc, posAcc, fusibleAcc];

  [
    ["MAKE",  "MAKE",  "MAKE",  "MAKE"],
    ["MODEL", "TYPE",  "MODEL", "MODEL"],
    ["MOC",   "MODEL", "TYPE",  "MOC"],
    ["SIZE",  "MOC",   "MOC",   "SIZE"],
    ["OTHER", "SIZE",  "SIZE",  "OTHER"],
    ["",      "OTHER", "AREA OF\nCLASSIFICATION", ""],
    ["",      "",      "OTHER", ""],
  ].forEach((labels) => {
    row4(ws, R, labels, labels.map((l, i) => l ? accP(fourAccs[i], l.replace(/\n/g, " ")) : ""));
    R++;
  });

  // ── FLOW CTRL / EXHAUST DIVERTER / JUNCTION BOX / AIRLOCK ────────────────
  mc(ws, R, 1, R, 2, "FLOW CONTROL VALVE",     HF(12), CA(), WHITE, BD_MED, 16.5);
  mc(ws, R, 3, R, 4, "EXHAUST DIVERTER VALVE", HF(12), CA(), WHITE, BD_MED, 16.5);
  mc(ws, R, 5, R, 6, "JUNCTION BOX",           HF(12), CA(), WHITE, BD_MED, 16.5);
  mc(ws, R, 7, R, 8, "AIRLOCK RELAY",          HF(12), CA(), WHITE, BD_MED, 16.5);
  R++;

  const fcAccs = [fcvAcc, edvAcc, jbAcc, airlockAcc];

  [
    ["MAKE",  "MAKE",  "MAKE",        "MAKE"],
    ["MODEL", "MODEL", "MODEL",       "MODEL"],
    ["MOC",   "MOC",   "MOC",         "MOC"],
    ["SIZE",  "SIZE",  "CABLE ENTRY", "SIZE"],
    ["OTHER", "OTHER", "AREA OF\nCLASSIFICATION", "OTHER"],
    ["",      "",      "OTHER",       ""],
  ].forEach((labels) => {
    row4(ws, R, labels, labels.map((l, i) => l ? accP(fcAccs[i], l.replace(/\n/g, " ")) : ""));
    R++;
  });

  // ── NOTES + SPECIAL TESTING + RELATED AUTOMATION + OPERATING CONDITIONS ───
  // Row headers
  mc(ws, R, 1, R, 4, "NOTES",                               HF(12), CA(), WHITE, BD_MED, 16.5);
  mc(ws, R, 5, R, 8, "SPECIAL TESTING & OTHER REQUIREMENT / (RELATED AUTOMATION PKG.):", HF(12), CA(), WHITE, BD_MED, 16.5);
  R++;

  // Notes value spans all 6 remaining rows on the left
  mc(ws, R, 1, R+5, 4, NA(d.notes), VF(12), LA(), WHITE, BD_MED, 15.75);

  // Row R: Special testing value | Related automation value
  mc(ws, R, 5, R, 8, NA(d.special_testing || d.related_automation_pkg), VF(12), LA(), WHITE, BD_MED, 15.75);
  R++;

  // Row R: Operating conditions header (cols 5-8)
  mc(ws, R, 5, R, 8,
    "OPERATING CONDITIONS  (if specified by customer)",
    HF(12), CA(), GREY, BD_MED, 16.5);
  R++;

  // Rows R to R+3: condition rows (cols 5-8 only; cols 1-4 are covered by notes merge)
  [
    ["Max. Press.", d.max_pressure],
    ["Max. Temp.",  d.max_temp],
    ["Min. Press.", d.min_pressure],
    ["Min. Temp.",  d.min_temp],
  ].forEach(([label, value]) => {
    mc(ws, R, 5, R, 6, label,     PF(12), LA(), GREY,  dataBorder(5, 6));
    mc(ws, R, 7, R, 8, NA(value), VF(12), LA(), WHITE, dataBorder(7, 8));
    ws.getRow(R).height = 15.75;
    R++;
  });

  R++; // advance past the notes merge (notes covered R to R+5, we've used 6 rows total)

  // ── TAG NUMBERS ───────────────────────────────────────────────────────────
  mc(ws, R, 1, R, 8, "Tag No (If Any)", HF(13), CA(), WHITE, BD_MED, 16.5);
  R++;

  for (let g = 0; g < 4; g++) {
    const c1 = g * 2 + 1, c2 = g * 2 + 2;
    mc(ws, R, c1, R, c1, "Sr. No.", PF(12), CA(), GREY, dataBorder(c1, c1));
    mc(ws, R, c2, R, c2, "Tag No.", PF(12), CA(), GREY, dataBorder(c2, c2));
  }
  ws.getRow(R).height = 15.75;
  R++;

  const tags     = d.tag_numbers || [];
  const TAG_ROWS = Math.max(Math.ceil(tags.length / 4), 15);

  for (let i = 0; i < TAG_ROWS; i++) {
    for (let g = 0; g < 4; g++) {
      const idx = g * TAG_ROWS + i;
      const c1 = g * 2 + 1, c2 = g * 2 + 2;
      mc(ws, R, c1, R, c1,
        idx < tags.length ? idx + 1 : "", VF(12), CA(), WHITE, dataBorder(c1, c1));
      mc(ws, R, c2, R, c2,
        tags[idx] !== undefined && String(tags[idx]).trim() !== "" ? String(tags[idx]) : (idx < tags.length ? "NA" : ""),
        VF(12), CA(), WHITE, dataBorder(c2, c2));
    }
    ws.getRow(R).height = 15.75;
    R++;
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Generates and downloads a TCDS Excel workbook.
 *
 * @param {object|object[]} input  One or more raw TCDS section records from the API.
 */
export const generateTCDSExcel = async (input) => {
  const raw      = Array.isArray(input) ? input : [input];
  const sections = raw
    .map(parseSection)
    .filter(Boolean)
    .filter((d) => d.section_number || d.section_title || d.make || d.series || d.type);

  if (sections.length === 0) throw new Error("No TCDS data provided");

  const wb = new ExcelJS.Workbook();
  wb.creator  = "TCDS Generator";
  wb.created  = new Date();
  wb.modified = new Date();

  // Load logo once and embed it in the workbook so all sheets can reference it
  const logoDef     = await loadLogoBuffer();
  let   logoImageId = null;

  if (logoDef) {
    try {
      logoImageId = wb.addImage({
        buffer:    logoDef.buffer,
        extension: logoDef.extension,
      });
    } catch (err) {
      console.warn("[generateTCDSExcel] Could not embed logo:", err.message);
    }
  }

  sections.forEach((d) => {
    const sheetName = (d.section_number || d.section_title || "SECTION")
      .replace(/[/\\?*[\]:]/g, "-")
      .substring(0, 31)
      .trim();
    const ws = wb.addWorksheet(sheetName);
    setupSheet(ws);
    drawSectionOnSheet(ws, d, logoImageId);
  });

  const buf      = await wb.xlsx.writeBuffer();
  const blob     = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const safeNo   = (sections[0].tcds_no || "TCDS").replace(/\s+/g, "_");
  const dateStr  = fmtDateFile(sections[0].tcds_release_date || "");
  const fileName = `TCDS_${safeNo}_${dateStr}.xlsx`;

  saveAs(blob, fileName);
  return fileName;
};

export default generateTCDSExcel;
